// labor.ts — 노무비(1ea) 순수 계산: 공정별 인원·C/T·임율.
//
// 백엔드 `apps/api/api/core/cost/labor.py` 와 동일한 수식/결과를 유지해야 한다
// (CLAUDE.md 패리티 — 한쪽 수정 시 양쪽 + 테스트 동시 수정).
//
// 임율은 상위 이슈(#1)의 단일 출처를 재사용한다 — 하드코딩 금지.
//   import { laborRateOf } from "@/lib/cost/master";  // laborRateOf(공정) -> ₩/hr
//
// 노무비계(total)는 #4(경비 — 여유율·C/T 라우팅 재사용)·#5(가공비)의 입력으로 연계된다:
//   import { laborCost, type LaborProcess, type LaborResult } from "@/lib/cost/labor";
//
// Excel 출처 CNC1/BOLT1/NUT1!B13:L21:
//   공정 노무비(L) = 인원(I) × C/T_sec(J) × 임율(K, ₩/hr) × (1 + 여유율 S) / 3600
//   임율 K         = laborRateOf(공정)   // #1 마스터 lookup
//   여유율 S       = 공정별 (Excel S 열), 기본 0.1 (10%) — 최종검사/포장 은 0
//   노무비계(L21)  = SUM(공정 노무비)
//
// 공정 라우팅은 부품별로 다르다:
//   - CNC  : 가공(본동) / 전수검사 / 최종검사·포장
//   - BOLT : 냉간단조(H) / 너링 / 전수검사 / 최종검사·포장
//   - NUT  : 냉간단조(F) / 나사산 / 전수검사 / 최종검사·포장

import { laborRateOf } from "@/lib/cost/master";

// ----- 상수 (Excel 시트 값 — magic number 금지) -----

// 여유율 기본값 S (Excel S 열). 공정별로 override 가능.
export const DEFAULT_ALLOWANCE = 0.1;

// 시간 환산: C/T 는 초(sec), 임율은 ₩/hr → / 3600.
export const SECONDS_PER_HOUR = 3600;

// 여유율 미적용(0) 공정 — 최종검사/포장. 공정명(master 임율 lookup 키)으로 식별한다.
// 이 공정의 노무비/경비는 ×(1+S) 가산 없이 계산된다(여유율 = 0). 단일 출처(이슈 #79).
export const NO_ALLOWANCE_PROCESSES: ReadonlySet<string> = new Set(["최종검사/포장"]);

// 공정명에 적용할 여유율을 반환한다. 여유율 미적용 공정(최종검사/포장)은 항상 0,
// 그 외 공정은 fallback(공정별 입력 → 없으면 part 기본 → DEFAULT_ALLOWANCE).
export function allowanceForProcess(processName: string, fallback = DEFAULT_ALLOWANCE): number {
  return NO_ALLOWANCE_PROCESSES.has(processName) ? 0 : fallback;
}

// ----- 입력 / 출력 -----

export interface LaborProcess {
  processName: string; // 공정명 (master 임율 lookup 키, #1 와 일치)
  headcount: number; // 인원 I
  ctSec: number; // C/T 작업시간(초) J
  allowance?: number; // 여유율 S, 기본 0.1
}

export interface LaborProcessResult {
  processName: string;
  headcount: number;
  ctSec: number;
  rate: number; // 임율 K (₩/hr) — laborRateOf(공정)
  allowance: number; // 여유율 S
  cost: number; // 공정 노무비 L (₩/ea)
}

export interface LaborResult {
  perProcess: LaborProcessResult[];
  total: number; // 노무비계 L21 = SUM(공정 노무비)
}

// 단일 공정 노무비 L = I × J × K × (1 + S) / 3600 (₩/ea).
export function processLaborCost(process: LaborProcess): number {
  const rate = laborRateOf(process.processName); // K (₩/hr) — #1 lookup
  // 최종검사/포장은 여유율 0 강제, 그 외는 공정별 입력/기본값. (#79)
  const allowance = allowanceForProcess(process.processName, process.allowance ?? DEFAULT_ALLOWANCE);
  return (
    (process.headcount * process.ctSec * rate * (1 + allowance)) / SECONDS_PER_HOUR
  );
}

// 공정 라우팅의 1ea 노무비를 산출 (Excel CNC1/BOLT1/NUT1!B13:L21).
export function laborCost(processes: LaborProcess[]): LaborResult {
  const perProcess: LaborProcessResult[] = processes.map((p) => {
    const rate = laborRateOf(p.processName); // K (₩/hr) — #1 lookup
    // 최종검사/포장은 여유율 0 강제, 그 외는 공정별 입력/기본값. (#79)
    const allowance = allowanceForProcess(p.processName, p.allowance ?? DEFAULT_ALLOWANCE);
    const cost = (p.headcount * p.ctSec * rate * (1 + allowance)) / SECONDS_PER_HOUR;
    return {
      processName: p.processName,
      headcount: p.headcount,
      ctSec: p.ctSec,
      rate,
      allowance,
      cost,
    };
  });

  const total = perProcess.reduce((acc, r) => acc + r.cost, 0);
  return { perProcess, total };
}
